import { useMutation } from '@tanstack/react-query'
import { type FormEvent, useRef, useState } from 'react'
import { isCredentialError, messageForError } from '../api/errors'
import { createSession } from '../api/sessions'
import { rememberEmailForReset } from '../auth/forgot-email'
import { readReturnPath } from '../auth/return-path'
import { track } from '../runtime/analytics'
import { useSession } from '../state/session'
import { AuthCard } from './ui/AuthCard'
import { Button } from './ui/Button'
import { FormError } from './ui/FormError'
import { TextField } from './ui/TextField'
import { TextLink } from './ui/TextLink'
import { useFocusWhenEnabled } from './ui/useFocusWhenEnabled'
import { useRedirectIfSignedIn } from './ui/useRedirectIfSignedIn'

export interface LoginFormProps {
  /**
   * Where a successful login lands when the visitor was not sent here from a
   * page that wants them back (`?next=/account`). Set with `data-redirect-to`.
   */
  redirectTo?: string
  forgotUrl?: string
  /** A hosted logo to show above the heading; omit for none. `data-logo-url`. */
  logoUrl?: string
  heading?: string
}

export function LoginForm({
  redirectTo = '/app',
  forgotUrl = '/forgot-password',
  logoUrl,
  heading = 'Sign In',
}: LoginFormProps) {
  const signIn = useSession((state) => state.signIn)

  const [identifier, setIdentifier] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const passwordRef = useRef<HTMLInputElement>(null)

  // A page that bounced the visitor here wins over the configured default.
  const destination = readReturnPath(window.location.search) ?? redirectTo

  const mutation = useMutation({
    mutationFn: () => createSession({ identifier, password }),
    onSuccess: (result) => {
      track({ kind: 'login', method: 'password' })
      signIn(result)
      window.location.assign(destination)
    },
    onError: (cause) => {
      setError(messageForError(cause))
      track({ kind: 'login_failed', reason: isCredentialError(cause) ? 'credentials' : 'other' })

      if (isCredentialError(cause)) {
        // Clear only the password — retyping the username every attempt is a
        // needless annoyance, and the field is the likely mistake anyway.
        setPassword('')
        focusPassword()
      } else {
        // Transport and misconfiguration failures are ours to debug; the user
        // gets a generic message either way.
        console.error('[codeverse] login request failed', cause)
      }
    },
  })

  // Someone who arrives already signed in has no business on the login page.
  useRedirectIfSignedIn(destination)

  const focusPassword = useFocusWhenEnabled(mutation.isPending, () => passwordRef.current)

  const canSubmit = identifier.trim() !== '' && password !== '' && !mutation.isPending

  function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!canSubmit) return
    setError(null)
    mutation.mutate()
  }

  return (
    <AuthCard heading={heading} logoUrl={logoUrl}>
      {/* A real <form> so Enter submits and password managers recognise it. */}
      <form onSubmit={onSubmit} noValidate>
        {error && <FormError data-testid="login-error">{error}</FormError>}

        <div className="mt-6 flex flex-col gap-3">
          <TextField
            label="Username or Email"
            placeholder="Username or Email"
            name="identifier"
            autoComplete="username"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            required
            value={identifier}
            onChange={(event) => setIdentifier(event.target.value)}
            disabled={mutation.isPending}
            data-testid="identifier"
          />

          <TextField
            ref={passwordRef}
            label="Password"
            placeholder="Password"
            name="password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            disabled={mutation.isPending}
            data-testid="password"
          />
        </div>

        <div className="mt-6">
          <Button type="submit" block disabled={!canSubmit} data-testid="submit">
            {mutation.isPending ? 'Signing In…' : heading}
          </Button>
        </div>

        <p className="mt-5 text-center">
          {/* The address they just typed goes with them, so they need not type it twice. */}
          <TextLink href={forgotUrl} onClick={() => rememberEmailForReset(identifier)}>
            Forgot username or password?
          </TextLink>
        </p>
      </form>
    </AuthCard>
  )
}
